import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Box, Collapse, useTheme} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import AppDialog from 'src/components-new/feedback/AppDialog'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import Button from 'src/components-new/primitives/Button'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'

interface DrugsLookupDialogProps {
  onClose: () => void
  onRowClick: (row: Record<string, any>) => void
}

const DEFAULT_PAGINATION: PaginationState = {page: 0, rowsPerPage: 10}

const COLUMNS: ColumnDef<Record<string, any>>[] = [
  {key: 'name', header: 'NOME', render: (row) => <>{row.name ?? '—'}</>},
]

/**
 * Same shape as DoctorsLookupDialog.tsx — used by ExportDialogNew's "Farmaco" field so it
 * matches the "Medico" field's search-by-dialog UX instead of an inline SelectNew dropdown.
 * Unlike doctors/medical centers, apiClient.getDrugs has no server-side page/size or count —
 * it always returns every match for the given search term — so pagination here slices that
 * array locally instead of round-tripping page/size to the backend.
 */
export default function DrugsLookupDialog({onClose, onRowClick}: DrugsLookupDialogProps): JSX.Element {
  const theme = useTheme()
  // Opened from inside ExportDialogNew (an AppDialog) — without this it renders behind
  // that dialog's Paper instead of above it, same reasoning as DoctorsLookupDialog.
  const dialogZIndex = theme.zIndex.modal + 2

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [name, setName] = useState('')
  const [appliedName, setAppliedName] = useState('')
  const [data, setData] = useState<Record<string, any>[]>([])
  const [status, setStatus] = useState<string>()
  const [pagination, setPagination] = useState<PaginationState>(DEFAULT_PAGINATION)
  // Guards against out-of-order responses: the initial, unfiltered fetch this dialog fires on
  // mount can still be in flight when the user opens "Filtra" and searches — without this, a
  // slower unfiltered response landing after a faster filtered one silently overwrites the
  // filtered result with the full list, making the search appear to do nothing.
  const latestRequestIdRef = useRef(0)

  const fetchData = useCallback(() => {
    const requestId = ++latestRequestIdRef.current
    apiClient.getDrugs(appliedName, setStatus).then((drugs: Record<string, any>[] = []) => {
      if (requestId !== latestRequestIdRef.current) return
      setData(drugs)
    })
  }, [appliedName])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSearch = () => {
    setAppliedName(name)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleClear = () => {
    setName('')
    setAppliedName('')
    setPagination(prev => ({...prev, page: 0}))
  }

  const pagedData = useMemo(() => {
    const start = pagination.page * pagination.rowsPerPage
    return data.slice(start, start + pagination.rowsPerPage)
  }, [data, pagination])

  return (
    <AppDialog
      actions={
        <>
          <Button onClick={handleClear} variant="secondary">Annulla</Button>
          <Button onClick={handleSearch} variant="primary">Cerca</Button>
        </>
      }
      maxWidth="md"
      onClose={onClose}
      sx={{zIndex: dialogZIndex}}
      open
      title="Farmaci"
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <Button
          onClick={() => setFiltersOpen(prev => !prev)}
          startIcon={<FilterListIcon />}
          sx={{alignSelf: 'flex-start'}}
          variant="secondary"
        >
          {filtersOpen ? 'Nascondi filtri' : 'Filtra'}
        </Button>

        <Collapse in={filtersOpen}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
              gap: 2,
              pb: 1,
            }}
          >
            <TextFieldNew fullWidth label="Nome" onChange={setName} value={name} />
          </Box>
        </Collapse>

        <DataTable<Record<string, any>>
          columns={COLUMNS}
          emptyMessage="Nessun farmaco trovato"
          keyField="_id"
          loading={status === STATUS.PENDING}
          onPaginationChange={setPagination}
          onRowClick={onRowClick}
          pagination={pagination}
          rows={pagedData}
          totalElements={data.length}
        />
      </Box>
    </AppDialog>
  )
}
